import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { TrendingUp, FileUp, Users, ShieldCheck, ShieldAlert, Zap, ArrowRight, Filter } from 'lucide-react';
import api from '@/api/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { toast } from 'sonner';

export default function FinanceDashboard() {
  const navigate = useNavigate();
  const [wallets, setWallets] = useState([]);
  const [summary, setSummary] = useState({
    total_wallets: 0,
    total_fleet_balance: 0,
    drivers_in_debt: 0,
    efficiency_score: '0.0',
  });
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterDebt, setFilterDebt] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const { data } = await api.get('/finance/stats');
      const payload = data.data || data;
      setWallets(Array.isArray(payload.wallets) ? payload.wallets : []);
      setSummary(
        payload.summary || {
          total_wallets: 0,
          total_fleet_balance: 0,
          drivers_in_debt: 0,
          efficiency_score: '0.0',
        }
      );
    } catch (err) {
      console.error(err);
      toast.error('Erro ao carregar estatísticas financeiras');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const filteredWallets = useMemo(() => {
    return wallets.filter((w) => {
      if (!w) return false;
      const fullName = `${w.first_name || ''} ${w.last_name || ''}`.toLowerCase();
      const walletId = w.id != null ? String(w.id) : '';
      const matchesSearch = fullName.includes(searchTerm.toLowerCase()) || walletId.includes(searchTerm);
      const matchesDebt = filterDebt ? parseFloat(w.current_balance || 0) < 0 : true;
      return matchesSearch && matchesDebt;
    });
  }, [wallets, searchTerm, filterDebt]);

  if (loading) {
    return (
      <div className="flex h-80 flex-col items-center justify-center">
        <Zap className="mb-4 h-10 w-10 animate-pulse text-primary" />
        <p className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">A carregar financeiro...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6 p-2 md:p-4">
      <div className="flex flex-col items-start justify-between gap-4 md:flex-row md:items-end">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Financial Core</h1>
          <p className="text-sm text-muted-foreground">Painel de liquidez e exposição de risco</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" onClick={() => navigate('/finance/history')}>
            Histórico
          </Button>
          <Button onClick={() => navigate('/finance/import')}>
            <FileUp className="mr-2 h-4 w-4" /> Sincronizar CSV
          </Button>
        </div>
      </div>

      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <Card>
          <CardHeader className="pb-2">
            <TrendingUp className="mb-2 h-5 w-5 text-primary" />
            <CardTitle className="text-xs font-medium uppercase text-muted-foreground">Liquidez Consolidada</CardTitle>
          </CardHeader>
          <CardContent>
            <div className={`text-2xl font-bold ${Number(summary.total_fleet_balance) >= 0 ? 'text-foreground' : 'text-destructive'}`}>
              {Number(summary.total_fleet_balance || 0).toLocaleString('pt-PT', { style: 'currency', currency: 'EUR' })}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <Users className="mb-2 h-5 w-5" />
            <CardTitle className="text-xs font-medium uppercase text-muted-foreground">Wallets Operacionais</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{summary.total_wallets || 0}</div>
          </CardContent>
        </Card>

        <Card className={summary.drivers_in_debt > 0 ? 'border-destructive bg-destructive/5' : ''}>
          <CardHeader className="pb-2">
            {summary.drivers_in_debt > 0 ? <ShieldAlert className="mb-2 h-5 w-5 text-destructive" /> : <ShieldCheck className="mb-2 h-5 w-5 text-emerald-600" />}
            <CardTitle className="text-xs font-medium uppercase text-muted-foreground">Risco de Exposição</CardTitle>
          </CardHeader>
          <CardContent>
            <div className={`text-2xl font-bold ${summary.drivers_in_debt > 0 ? 'text-destructive' : 'text-foreground'}`}>
              {summary.drivers_in_debt || 0}
            </div>
          </CardContent>
        </Card>

        <Card className="bg-primary text-primary-foreground">
          <CardHeader className="pb-2">
            <Zap className="mb-2 h-5 w-5" />
            <CardTitle className="text-xs font-medium uppercase text-primary-foreground/80">Performance Score</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{summary.efficiency_score || '0.0'}%</div>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
          <div>
            <CardTitle>Saldos Ativos</CardTitle>
            <p className="text-xs text-muted-foreground">Omni-Ledger Consolidado</p>
          </div>
          <div className="flex flex-col gap-3 md:flex-row md:items-center">
            <div className="flex gap-2 rounded-lg border p-1">
              <Button size="sm" variant={!filterDebt ? 'default' : 'ghost'} onClick={() => setFilterDebt(false)}>
                Todos
              </Button>
              <Button size="sm" variant={filterDebt ? 'destructive' : 'ghost'} onClick={() => setFilterDebt(true)}>
                Devedores
              </Button>
            </div>
            <div className="relative w-full md:w-72">
              <Filter className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                placeholder="Pesquisar operador ou wallet..."
                className="pl-9"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm">
              <thead>
                <tr className="border-b">
                  <th className="py-3 pl-4 font-medium text-muted-foreground">Operador / Wallet ID</th>
                  <th className="py-3 pr-4 text-right font-medium text-muted-foreground">Saldo Disponível</th>
                  <th className="py-3 text-center font-medium text-muted-foreground">Status de Risco</th>
                  <th className="py-3 pr-4 text-right font-medium text-muted-foreground">Ações</th>
                </tr>
              </thead>
              <tbody>
                {filteredWallets.length > 0 ? (
                  filteredWallets.map((wallet) => (
                    <tr key={wallet.driver_id || wallet.wallet_id || wallet.id} className="border-b last:border-0 hover:bg-muted/40">
                      <td className="py-4 pl-4">
                        <div className="flex items-center gap-3">
                          <div className="flex h-10 w-10 items-center justify-center rounded-full bg-slate-900 text-primary-foreground font-bold text-xs">
                            {(wallet.first_name?.[0] || '?')}{(wallet.last_name?.[0] || '?')}
                          </div>
                          <div>
                            <p className="font-semibold">{wallet.first_name || 'Desconhecido'} {wallet.last_name || ''}</p>
                            <p className="text-xs text-muted-foreground">Ledger ID: {String(wallet.wallet_id || wallet.id || 0).padStart(5, '0')}</p>
                          </div>
                        </div>
                      </td>
                      <td className="py-4 pr-4 text-right">
                        <span className={`text-lg font-bold ${parseFloat(wallet.current_balance || 0) >= 0 ? 'text-foreground' : 'text-destructive'}`}>
                          {parseFloat(wallet.current_balance || 0).toLocaleString('pt-PT', { minimumFractionDigits: 2 })}€
                        </span>
                      </td>
                      <td className="py-4 text-center">
                        <span className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-xs font-semibold ${
                          parseFloat(wallet.current_balance || 0) >= 0
                            ? 'border-emerald-200 bg-emerald-100 text-emerald-800'
                            : 'border-red-200 bg-red-100 text-red-800'
                        }`}>
                          <span className={`h-1.5 w-1.5 rounded-full ${parseFloat(wallet.current_balance || 0) >= 0 ? 'bg-emerald-500' : 'bg-red-600'}`} />
                          {parseFloat(wallet.current_balance || 0) >= 0 ? 'Solvência Total' : 'Risco de Exposição'}
                        </span>
                      </td>
                      <td className="py-4 pr-4 text-right">
                        <Button
                          variant="outline"
                          size="icon"
                          onClick={() => navigate(`/finance/wallet/${wallet.driver_id}`)}
                        >
                          <ArrowRight className="h-4 w-4" />
                        </Button>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={4} className="py-8 text-center text-sm text-muted-foreground">
                      Nenhum registo financeiro localizado.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
